import DtoolsPdfPrimaryProductRow from "@/components/sales/dtools/DtoolsPdfPrimaryProductRow";
import DtoolsPdfCompactLineTable from "@/components/sales/dtools/DtoolsPdfCompactLineTable";
import { useDtoolsPdfPackageMode } from "@/components/sales/dtools/DtoolsPdfPackageContext";
import { useDtoolsProposalDisplay } from "@/components/sales/dtools/DtoolsProposalDisplayContext";
import { DtoolsReviewPrimaryBlock } from "@/components/sales/dtools/DtoolsReviewLineCard";
import { formatDtoolsMoney } from "@/lib/dtoolsJsonFormat";
import { PROPOSAL_COPY_FONT, PROPOSAL_TEXT } from "@/lib/proposalTheme";
import { PDF_SECTION_TITLE_CLASS } from "@/lib/proposalPdfTypography";
import {
  buildOptionComponentGroups,
  collectOptionPrimaryComponentBlocks,
  collectPdfPrimaryComponentBlocks,
  sortComponentLinesForDisplay,
} from "@shared/dtoolsOptionComponentGroups";
import type { DtoolsReviewSystem } from "@shared/dtoolsStagingTransform";

type Props = {
  system: DtoolsReviewSystem;
};

export default function DtoolsOptionComponentGroupsSection({ system }: Props) {
  const pdfPackageMode = useDtoolsPdfPackageMode();
  const display = useDtoolsProposalDisplay();
  const primaryBlocks = pdfPackageMode
    ? collectPdfPrimaryComponentBlocks(system)
    : collectOptionPrimaryComponentBlocks(system);
  const supportingLines = pdfPackageMode
    ? sortComponentLinesForDisplay(buildOptionComponentGroups(system).supportingLines)
    : [];

  if (primaryBlocks.length === 0 && supportingLines.length === 0) return null;

  return (
    <div
      className={pdfPackageMode ? "space-y-3" : "space-y-6"}
      data-testid="dtools-option-component-groups"
      style={{ fontFamily: PROPOSAL_COPY_FONT }}
    >
      {primaryBlocks.length > 0 ? (
        <section className="space-y-1" data-pdf-primary-components>
          <h3
            className={pdfPackageMode ? PDF_SECTION_TITLE_CLASS : "text-sm font-semibold uppercase tracking-[0.14em]"}
            style={{ color: PROPOSAL_TEXT }}
          >
            Primary Components
          </h3>
          {pdfPackageMode ? (
            <div className="divide-y divide-[#1A2744]/10 border-t border-[#1A2744]/10">
              {primaryBlocks.map((block) => (
                <DtoolsPdfPrimaryProductRow
                  key={block.line.id}
                  line={block.line}
                  hidePrice={display.hideItemPricing}
                  priceFormatted={formatDtoolsMoney(block.line.lineTotal)}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {primaryBlocks.map((block) => (
                <DtoolsReviewPrimaryBlock key={block.line.id} block={block} />
              ))}
            </div>
          )}
        </section>
      ) : null}

      {pdfPackageMode && supportingLines.length > 0 ? (
        <DtoolsPdfCompactLineTable
          title="Supporting Components"
          lines={supportingLines}
          testId="dtools-pdf-supporting-components"
        />
      ) : null}
    </div>
  );
}
